import {DATA_TYPES, HEADER_NAME} from '../../constants/constants'
import {
    getNextIdForPrefix,
    getSectionNameByPointer,
    ignoredTemplateOutlineTypes,
    invalidOutlineRetryAmount,
    type PointerPredicate,
    reportBIError,
    unsectionedOutlineSectionName
} from './aiExtensionContent'
import type {CreateExtArgs, EnvironmentContext} from '@wix/document-manager-core'
import type {
    OverrideOrDefaultData,
    Pointer,
    IdMap,
    UserRequestInfo,
    NewTextPointerItem,
    UpdateComponentBuilderContentParams
} from '@wix/document-services-types'
import type {StructureExtensionAPI} from '../structure'
import type {ComponentDefinitionExtensionAPI} from '../componentDefinition'
import {getReportableFromError, ReportableError} from '@wix/document-manager-utils'
import {extractTextFromHTML, fetchBuilderNewOutlineFromPromptHub, insertTextInHtml} from './aiExtensionUtils'
import {
    aiBuilderInjectionResultConsecutiveErrorMessage,
    aiBuilderInjectionResultConsecutiveErrorType,
    aiContentBadJsonErrorMessage,
    aiContentBadJsonErrorType,
    aiContentUnexpectedError,
    aiContentUnexpectedErrorMessage
} from './constants'
import {predicateBuilderComp} from './aiBuilderUtils'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import type {BuilderStyleExtensionAPI} from '../builderComponents/builderStyle'
import type {DataExtensionAPI} from '../data'
import type {LoggerExtAPI} from '../logger'

const PROPERTY_TYPES = {
    RICH_TEXT: 'richText',
    TEXT: 'text'
} as const

interface PropertyHandler {
    extract(data: any): string
    update(oldValue: any, newText: string): any
}

const PROPERTY_HANDLERS: Record<string, PropertyHandler> = {
    [PROPERTY_TYPES.RICH_TEXT]: {
        extract: (data: string) => extractTextFromHTML(data),
        update: (oldValue: any, newText: string) => ({
            text: insertTextInHtml(oldValue, newText, false),
            type: 'Builder.RichText'
        })
    },
    [PROPERTY_TYPES.TEXT]: {
        extract: (data: string) => data,
        update: (oldValue: any, newText: string) => newText
    }
}

const BUILDER_PROPERTY_CONFIG = [
    {prop: PROPERTY_TYPES.TEXT},
    {
        prop: PROPERTY_TYPES.RICH_TEXT,
        extract: (data: any) => data.text
    }
]

const BUILDER_TEXT_ALIGN_STYLE_CONFIG = [{prop: 'textAlign'}]

const extractPropertyData = (prop: string, data: any): string => {
    const handler = PROPERTY_HANDLERS[prop]
    return handler ? handler.extract(data) : data
}

const createUpdateData = (prop: string, oldValue: any, newText: string) => {
    const handler = PROPERTY_HANDLERS[prop]
    const updatedValue = handler ? handler.update(oldValue, newText) : newText

    return {
        [prop]: updatedValue
    }
}

interface BuilderInjectionValidationResult {
    isValid: boolean
    reason: string
    invalidTargets: string[]
}

export interface BuilderOutline {
    [section: string]: {[componentName: string]: BuilderComponentData}
}

export interface BuilderComponentData {
    value: string
    metaData: any
    length: number
    prop: string
}

export interface BuilderOutlineWithIdMapAndTargets {
    outline: BuilderOutline
    idMap: IdMap
    targets: string[]
    oldValues: Record<string, any>
    textAlignStyleItems: TextAlignStyleItem[]
}

type Direction = 'rtl' | 'ltr' | undefined

export interface BuilderAiInjectionResult {
    newTexts: Record<string, string>
    textDirection?: Direction
}

export interface TextAlignStyleItem extends OverrideOrDefaultData {
    pointer: Pointer
}

export interface BuilderAiInjectionResultWithIdMapAndOldValues {
    builderAiInjectionResult: BuilderAiInjectionResult
    idMap: IdMap
    oldValues: Record<string, any>
    textAlignStyleItems: TextAlignStyleItem[]
}

const validateBuilderInjectionResult = (
    newTexts: Record<string, string> | undefined,
    targets: string[],
    idMap: IdMap
): BuilderInjectionValidationResult => {
    const invalidTargets = targets.filter(
        target => !newTexts?.hasOwnProperty(target) || typeof newTexts[target] !== 'string'
    )
    return {
        isValid: invalidTargets.length === 0,
        reason:
            invalidTargets.length === 0
                ? ''
                : `Failed to generate ${invalidTargets.length} texts for ${invalidTargets
                      .map(target => idMap[target].pointer.id)
                      .join(', ')}`,
        invalidTargets
    }
}

const applyTextDirectionToStyle = (
    createExtArgs: CreateExtArgs,
    textDirection: Direction,
    textAlignStyleItems: TextAlignStyleItem[]
): void => {
    const {builderStyle, innerElements, logger} = createExtArgs.extensionAPI as BuilderStyleExtensionAPI &
        InnerElementsExtensionAPI &
        LoggerExtAPI
    const alignmentValue = textDirection === 'rtl' ? 'end' : 'start'
    textAlignStyleItems.forEach(item => {
        if (item.value === 'center') return
        const partialStyle = {[item.whereToWriteInDM]: {[item.prop]: alignmentValue}}
        try {
            if (item.path?.length) {
                innerElements.setInnerElementStyle(item.pointer as any, item.path, partialStyle)
            } else {
                builderStyle.component.updateCSSStyle(item.pointer as any, partialStyle)
            }
        } catch (error: any) {
            const reportableError = new ReportableError({
                errorType: 'aiStyleInjection',
                message: error.message,
                extras: {originalError: error, pointer: item.pointer, prop: item.prop}
            })
            logger.captureError(reportableError)
        }
    })
}

const buildNewTextsPointer = (result: BuilderAiInjectionResultWithIdMapAndOldValues): NewTextPointerItem[] =>
    Object.entries(result.builderAiInjectionResult.newTexts).map(([componentId, newText]) => {
        const {pointer, prop, path, nestedDataPath} = result.idMap[componentId]
        const oldRawValue = result.oldValues[pointer.id]
        const oldText = extractPropertyData(prop, oldRawValue[prop])

        return {pointer, path, nestedDataPath, newText, oldText}
    })

export const applyBuilderInjection = (
    createExtArgs: CreateExtArgs,
    {idMap, builderAiInjectionResult, oldValues, textAlignStyleItems}: BuilderAiInjectionResultWithIdMapAndOldValues
) => {
    const {data, logger} = createExtArgs.extensionAPI as DataExtensionAPI & LoggerExtAPI
    const {newTexts, textDirection} = builderAiInjectionResult
    Object.entries(newTexts).forEach(([componentId, newText]) => {
        const {pointer, prop, path, nestedDataPath} = idMap[componentId]
        if (!pointer) return
        const updateData = createUpdateData(prop, oldValues[pointer.id][prop], newText)
        try {
            data.injectData({pointer, path, nestedDataPath, partialNewData: updateData})
        } catch (error: any) {
            const reportableError = new ReportableError({
                errorType: 'aiDataInjection',
                message: error.message,
                extras: {
                    originalError: error,
                    pointer,
                    prop,
                    path,
                    nestedDataPath
                }
            })
            logger.captureError(reportableError)
        }
    })

    if (textDirection !== undefined && textAlignStyleItems.length > 0) {
        applyTextDirectionToStyle(createExtArgs, textDirection, textAlignStyleItems)
    }
}

export const getPageOutlineAndTargetsForBuilder = (
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer
): BuilderOutlineWithIdMapAndTargets => {
    const {pointers} = createExtArgs
    const {components} = createExtArgs.extensionAPI as StructureExtensionAPI
    const {componentDefinition} = createExtArgs.extensionAPI as ComponentDefinitionExtensionAPI
    const {manifest} = createExtArgs.extensionAPI as ManifestExtensionAPI

    const getContentRecursively = (
        compPointer: Pointer,
        outline: BuilderOutline,
        typeCounter: Record<string, number>,
        idMap: IdMap,
        oldValues: Record<string, any>,
        textAlignStyleItems: TextAlignStyleItem[],
        outlineSectionName: string | undefined,
        pointerPredicate: PointerPredicate,
        selectedComponent: Pointer,
        targets: string[],
        addToTargets: boolean = false
    ): void => {
        if (compPointer.id === selectedComponent.id) addToTargets = true
        const type = components.getComponentType(compPointer)

        if (ignoredTemplateOutlineTypes.has(type)) {
            return
        }

        if (componentDefinition.isContainer(type)) {
            const containerName =
                componentDefinition.isSection(type) &&
                getSectionNameByPointer(createExtArgs.extensionAPI, compPointer, DATA_TYPES.naming)
            const containerOutlineSectionName = containerName
                ? getNextIdForPrefix(containerName, typeCounter)
                : outlineSectionName

            const children = pointers.structure.getChildren(compPointer)
            for (const childPointer of children) {
                getContentRecursively(
                    childPointer,
                    outline,
                    typeCounter,
                    idMap,
                    oldValues,
                    textAlignStyleItems,
                    containerOutlineSectionName,
                    pointerPredicate,
                    selectedComponent,
                    targets,
                    addToTargets
                )
            }
        }

        if (pointerPredicate && !pointerPredicate(compPointer)) return

        const {data: dataContexts, style: styleContexts} = manifest.getManifestItemsFromSeveralSpaces(
            compPointer,
            {
                data: {types: BUILDER_PROPERTY_CONFIG},
                style: {types: BUILDER_TEXT_ALIGN_STYLE_CONFIG}
            },
            true
        )
        styleContexts?.forEach(context => {
            textAlignStyleItems.push({...context, pointer: compPointer})
        })
        dataContexts?.forEach(context => {
            const {prop, path, metaData, nestedDataPath} = context
            let {value: data} = context
            oldValues[compPointer.id] ??= {}
            oldValues[compPointer.id][prop] = data

            data = extractPropertyData(prop, data)

            const mappedId = getNextIdForPrefix('comp', typeCounter)
            idMap[mappedId] = {pointer: compPointer, prop, path, nestedDataPath}
            const outlineSectionNameOrDefault =
                outlineSectionName ??
                (pointers.structure.isInMasterPage(compPointer) ? HEADER_NAME : unsectionedOutlineSectionName)
            outline[outlineSectionNameOrDefault] ??= {}
            outline[outlineSectionNameOrDefault][mappedId] = {value: data, length: data?.length, metaData, prop}
            if (addToTargets) targets.push(mappedId)
        })
    }

    const outline: BuilderOutline = {}
    const typeCounter: Record<string, number> = {}
    const idMap = {}
    const oldValues = {}
    const textAlignStyleItems: TextAlignStyleItem[] = []

    const predicate = (pointer: Pointer): boolean => {
        const componentType = components.getComponentType(pointer)
        return predicateBuilderComp(createExtArgs.extensionAPI, componentType)
    }

    const pagePointer = pointers.structure.getPageOfComponent(componentPointer)
    const targets: string[] = []
    getContentRecursively(
        pagePointer,
        outline,
        typeCounter,
        idMap,
        oldValues,
        textAlignStyleItems,
        undefined,
        predicate,
        componentPointer,
        targets
    )
    return {outline, idMap, targets, oldValues, textAlignStyleItems}
}

interface BuilderInjectionResultWithFailures extends BuilderAiInjectionResultWithIdMapAndOldValues {
    failures: string[]
}

export const getComponentInjectionForBuilder = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    componentPointer: Pointer,
    siteType: string,
    businessName: string,
    siteDescription: string,
    userRequest: string,
    userRequestInfo?: UserRequestInfo,
    signal?: AbortSignal
): Promise<BuilderInjectionResultWithFailures> => {
    const {serverFacade} = environmentContext
    const {extensionAPI} = createExtArgs
    const {logger} = extensionAPI as LoggerExtAPI

    const {outline, idMap, targets, oldValues, textAlignStyleItems} = getPageOutlineAndTargetsForBuilder(
        createExtArgs,
        componentPointer
    )
    let builderAiInjectionResult: BuilderAiInjectionResult | null = null
    let lastValidation: BuilderInjectionValidationResult | null = null

    let apiCallCount = 0
    let lastError: Error | null = null

    try {
        while (apiCallCount <= invalidOutlineRetryAmount) {
            if (signal?.aborted) {
                throw signal.reason ?? new DOMException('Aborted', 'AbortError')
            }
            const completionResponse = await fetchBuilderNewOutlineFromPromptHub(
                serverFacade,
                userRequest,
                outline,
                targets,
                siteType,
                businessName,
                siteDescription,
                userRequestInfo,
                signal
            )

            const completionText = completionResponse.generatedTexts[0]
            apiCallCount += 1

            try {
                builderAiInjectionResult = JSON.parse(completionText!)
            } catch (e) {
                lastError = new ReportableError({
                    errorType: aiContentBadJsonErrorType,
                    message: aiContentBadJsonErrorMessage,
                    extras: {
                        completionText
                    }
                })
                continue
            }
            lastValidation = validateBuilderInjectionResult(builderAiInjectionResult!.newTexts, targets, idMap)
            if (!lastValidation.isValid) continue
            return {
                builderAiInjectionResult: builderAiInjectionResult!,
                idMap,
                oldValues,
                textAlignStyleItems,
                failures: []
            }
        }
        if (!builderAiInjectionResult) {
            throw (
                lastError ??
                new ReportableError({
                    errorType: aiContentUnexpectedError,
                    message: aiContentUnexpectedErrorMessage
                })
            )
        }

        const {reason, isValid, invalidTargets} = lastValidation!

        reportBIError(
            logger,
            aiBuilderInjectionResultConsecutiveErrorType,
            aiBuilderInjectionResultConsecutiveErrorMessage,
            builderAiInjectionResult!,
            {reason, isValid}
        )

        const failures = [reason]
        const validTargets = targets.filter(t => !invalidTargets.includes(t))
        const validNewTexts = Object.fromEntries(validTargets.map(t => [t, builderAiInjectionResult!.newTexts[t]]))
        return {
            builderAiInjectionResult: {
                ...builderAiInjectionResult,
                newTexts: validNewTexts
            },
            idMap,
            oldValues,
            textAlignStyleItems,
            failures
        }
    } catch (e: any) {
        if (signal?.aborted) {
            throw e
        }
        const reportedError = getReportableFromError(e, {
            errorType: e.errorType ?? aiContentUnexpectedError,
            tags: {
                aiContentInjection: true
            }
        })
        logger.captureError(reportedError)
        throw reportedError
    }
}

export const updateComponentBuilderContentFlow = async (
    environmentContext: EnvironmentContext,
    createExtArgs: CreateExtArgs,
    params: UpdateComponentBuilderContentParams
): Promise<void> => {
    const {
        componentPointer,
        siteType,
        businessName,
        siteDescription,
        userRequest,
        userRequestInfo,
        onSuccess,
        onError,
        signal
    } = params
    const {logger} = createExtArgs.extensionAPI as LoggerExtAPI
    try {
        const result = await getComponentInjectionForBuilder(
            environmentContext,
            createExtArgs,
            componentPointer,
            siteType,
            businessName,
            siteDescription,
            userRequest,
            userRequestInfo,
            signal
        )
        if (signal?.aborted) {
            logger.breadcrumb('builder content generation aborted', {
                extras: {caller: 'updateComponentBuilderContentFlow', stage: 'after-getComponentInjection'}
            })
            return
        }
        applyBuilderInjection(createExtArgs, result)
        if (result.failures.length > 0) {
            onError?.({failures: result.failures})
        } else {
            onSuccess?.({newTextsPointer: buildNewTextsPointer(result)})
        }
    } catch (e: any) {
        if (signal?.aborted) {
            logger.breadcrumb('builder content generation aborted', {
                extras: {caller: 'updateComponentBuilderContentFlow', stage: 'catch'}
            })
            return
        }
        onError?.(e)
    }
}
